import type { Post } from "@microbeta/common-types";
import config from "../../../config";
import { httpClient as apiClient } from "../../../shared/utils/httpClientInstance";

const BASE_URL = config.VITE_SOCIAL_SERVICE_URL;

export interface FeedPost extends Post {
  routeName: string | null;
  routeGrade: number | null;
  areaName: string | null;
  areaSlug: string | null;
  modelSlug: string | null;
  reactionCount: number;
  reactionTypes?: string[];
  commentCount: number;
  hasReacted: boolean;
  user: {
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  };
  media: Array<{
    id: string;
    mediaUrl: string;
    mediaType: string;
    sortOrder: number;
  }>;
}

export interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
}

export interface FeedResponse {
  feed: FeedPost[];
}

export interface UserSearchResult {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  connectionStatus: "none" | "pending" | "accepted" | "blocked";
}

export interface UserSearchResponse {
  users: UserSearchResult[];
}

export interface FriendItem {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface FriendsResponse {
  friends: FriendItem[];
}

export interface NotificationItem {
  id: string;
  type:
    | "friend_request"
    | "friend_accepted"
    | "post_like"
    | "post_comment"
    | "post_mention";
  referenceId: string | null;
  readAt: string | null;
  createdAt: string;
  actor: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  };
}

export interface NotificationsResponse {
  notifications: NotificationItem[];
}

export interface ClimbingStats {
  totalSends: number;
  hardestGrade: number | null;
  areasVisited: number;
  friendsCount: number;
  gradeDistribution: Record<number, number>;
}

export interface PendingRequestItem {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  requestedAt: string;
}

export interface PendingRequestsResponse {
  pendingRequests: PendingRequestItem[];
}

export interface PublicProfile {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  bio: string | null;
  profileImages: Record<string, unknown> | null;
  connectionStatus: "none" | "pending" | "accepted" | "blocked" | "self";
  isOwnProfile: boolean;
  stats: {
    friendsCount: number;
    totalSends: number;
    hardestGrade: number | null;
    areasVisited: number;
  };
}

export interface PublicProfileResponse {
  profile: PublicProfile;
}

export const socialApi = {
  // --- Feed ---
  getFeed: async (
    limit = 20,
    offset = 0,
    targetUserId?: string
  ): Promise<FeedResponse> => {
    let url = `${BASE_URL}/social/feed?limit=${limit}&offset=${offset}`;
    if (targetUserId) {
      url += `&userId=${targetUserId}`;
    }
    const response = await apiClient.get<unknown>(url);
    return (
      (response as { data?: FeedResponse })?.data ?? (response as FeedResponse)
    );
  },

  // --- Posts ---
  createPost: async (data: {
    content?: string;
    routeId?: string;
    modelId?: string;
    areaId?: string;
    mediaUrl?: string;
    privacy?: "public" | "friends_only" | "private";
  }) => {
    const response = await apiClient.post<unknown>(
      `${BASE_URL}/social/posts`,
      data
    );
    return (response as { data?: unknown })?.data ?? response;
  },

  updatePost: async (postId: string, data: { content: string }) => {
    const response = await apiClient.put<unknown>(
      `${BASE_URL}/social/posts/${postId}`,
      data
    );
    return (response as { data?: unknown })?.data ?? response;
  },

  getComments: async (postId: string): Promise<{ comments: Comment[] }> => {
    const response = await apiClient.get<unknown>(
      `${BASE_URL}/social/posts/${postId}/comments`
    );
    return (
      (response as { data?: { comments: Comment[] } })?.data ??
      (response as { comments: Comment[] })
    );
  },

  createComment: async (
    postId: string,
    content: string
  ): Promise<{ comment: Comment }> => {
    const response = await apiClient.post<unknown>(
      `${BASE_URL}/social/posts/${postId}/comments`,
      { content }
    );
    return (
      (response as { data?: { comment: Comment } })?.data ??
      (response as { comment: Comment })
    );
  },

  toggleReaction: async (
    postId: string,
    reactionType:
      | "like"
      | "stoke"
      | "fire"
      | "heart"
      | "laugh"
      | "skull"
      | "wow"
      | "sad"
      | "angry" = "stoke"
  ): Promise<{ reacted: boolean; reactionType: string }> => {
    const response = await apiClient.post<unknown>(
      `${BASE_URL}/social/posts/${postId}/reactions`,
      { reactionType }
    );
    return (
      (response as { data?: { reacted: boolean; reactionType: string } })
        ?.data ?? (response as { reacted: boolean; reactionType: string })
    );
  },

  deletePost: async (postId: string): Promise<void> => {
    await apiClient.del(`${BASE_URL}/social/posts/${postId}`);
  },

  // --- Users / Connections ---
  searchUsers: async (query: string): Promise<UserSearchResponse> => {
    const response = await apiClient.get<unknown>(
      `${BASE_URL}/social/users/search?q=${encodeURIComponent(query)}`
    );
    return (
      (response as { data?: UserSearchResponse })?.data ??
      (response as UserSearchResponse)
    );
  },

  getFriends: async (): Promise<FriendsResponse> => {
    const response = await apiClient.get<unknown>(
      `${BASE_URL}/social/users/friends`
    );
    return (
      (response as { data?: FriendsResponse })?.data ??
      (response as FriendsResponse)
    );
  },

  getPendingRequests: async (): Promise<PendingRequestsResponse> => {
    const response = await apiClient.get<unknown>(
      `${BASE_URL}/social/users/pending-requests`
    );
    return (
      (response as { data?: PendingRequestsResponse })?.data ??
      (response as PendingRequestsResponse)
    );
  },

  getPublicProfile: async (handle: string): Promise<PublicProfileResponse> => {
    const response = await apiClient.get<unknown>(
      `${BASE_URL}/social/users/profile/${encodeURIComponent(handle)}`
    );
    return (
      (response as { data?: PublicProfileResponse })?.data ??
      (response as PublicProfileResponse)
    );
  },

  sendFriendRequest: async (
    targetUserId: string
  ): Promise<{ status: string }> => {
    const response = await apiClient.post<unknown>(
      `${BASE_URL}/social/users/connections`,
      { targetUserId }
    );
    return (
      (response as { data?: { status: string } })?.data ??
      (response as { status: string })
    );
  },

  acceptFriendRequest: async (userId: string): Promise<{ status: string }> => {
    const response = await apiClient.post<unknown>(
      `${BASE_URL}/social/users/connections/${userId}/accept`,
      {}
    );
    return (
      (response as { data?: { status: string } })?.data ??
      (response as { status: string })
    );
  },

  rejectFriendRequest: async (userId: string): Promise<{ status: string }> => {
    const response = await apiClient.post<unknown>(
      `${BASE_URL}/social/users/connections/${userId}/reject`,
      {}
    );
    return (
      (response as { data?: { status: string } })?.data ??
      (response as { status: string })
    );
  },

  removeFriend: async (userId: string): Promise<{ status: string }> => {
    const response = await apiClient.del(
      `${BASE_URL}/social/users/connections/${userId}`
    );
    return (
      (response as { data?: { status: string } })?.data ??
      (response as { status: string })
    );
  },

  // --- Climbing Stats ---
  getClimbingStats: async (
    userId?: string
  ): Promise<{ stats: ClimbingStats }> => {
    const path = userId
      ? `${BASE_URL}/social/users/stats/${userId}`
      : `${BASE_URL}/social/users/stats`;
    const response = await apiClient.get<unknown>(path);
    return (
      (response as { data?: { stats: ClimbingStats } })?.data ??
      (response as { stats: ClimbingStats })
    );
  },

  // --- Notifications ---
  getNotifications: async (
    limit = 30,
    offset = 0
  ): Promise<NotificationsResponse> => {
    const response = await apiClient.get<unknown>(
      `${BASE_URL}/social/notifications?limit=${limit}&offset=${offset}`
    );
    return (
      (response as { data?: NotificationsResponse })?.data ??
      (response as NotificationsResponse)
    );
  },

  getUnreadNotificationCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get<unknown>(
      `${BASE_URL}/social/notifications/unread-count`
    );
    return (
      (response as { data?: { count: number } })?.data ??
      (response as { count: number })
    );
  },

  markNotificationRead: async (id: string): Promise<{ status: string }> => {
    const response = await apiClient.patch<unknown>(
      `${BASE_URL}/social/notifications/${id}/read`,
      {}
    );
    return (
      (response as { data?: { status: string } })?.data ??
      (response as { status: string })
    );
  },

  markAllNotificationsRead: async (): Promise<{ status: string }> => {
    const response = await apiClient.patch<unknown>(
      `${BASE_URL}/social/notifications/read-all`,
      {}
    );
    return (
      (response as { data?: { status: string } })?.data ??
      (response as { status: string })
    );
  },
};
